import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/components/ui/use-toast";
import { useMutation } from "@tanstack/react-query";
import type { PresignedClient } from "@/lib/file-storage/types";
import type { AttachmentItem, PendingDelete } from "../../types/attachments";
import { useAsyncOperationProgress } from "@/components/progress/useAsyncOperationProgress";

interface QuestionnaireAttachmentDeletionProps {
  presignedClient: PresignedClient;
  name: string;
  /** Po udanym `complete-delete`; trzeci argument to body odpowiedzi (skany umowy aktualizują z niego cache) */
  onFileDeleted: (
    s3Key: string,
    index: number,
    completionResponse: unknown,
  ) => void;
  /** Opis przyczyny błędu do toastu; `null` = generyczny `fileAttachments.deleteError` */
  describeDeleteError?: (error: unknown) => string | null;
  /** Raz po nieudanym usunięciu, po toaście (np. odświeżenie szczegółów po nieświeżej stronie) */
  onDeleteError?: (error: unknown) => void;
}

/** Wynik udanego usunięcia: dane z dialogu plus body odpowiedzi backendu. */
type DeleteResult = PendingDelete & { completionResponse: unknown };

export const useQuestionnaireAttachmentDeletion = ({
  presignedClient,
  name,
  onFileDeleted,
  describeDeleteError,
  onDeleteError,
}: QuestionnaireAttachmentDeletionProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(
    null,
  );
  const operationProgress = useAsyncOperationProgress();

  const deleteMutation = useMutation({
    mutationFn: async (deleteData: PendingDelete): Promise<DeleteResult> => {
      operationProgress.initialize([
        {
          id: deleteData.s3Key,
          label: deleteData.filename,
        },
      ]);
      operationProgress.startProcessing();
      operationProgress.setCurrentItem(deleteData.filename);

      try {
        const completionResponse = await presignedClient.remove(name, {
          attachmentId: deleteData.attachmentId,
          filename: deleteData.filename,
          s3Key: deleteData.s3Key,
        });
        operationProgress.markItemProcessed(deleteData.s3Key, true);
        return { ...deleteData, completionResponse };
      } catch (error) {
        operationProgress.markItemProcessed(deleteData.s3Key, false);
        throw error;
      } finally {
        operationProgress.complete();
      }
    },
    onSuccess: (result) => {
      onFileDeleted(result.s3Key, result.index, result.completionResponse);
      setPendingDelete(null);
    },
    onError: (error, deleteData) => {
      const reason = describeDeleteError?.(error);
      toast({
        title: t("fileAttachments.deleteErrorTitle"),
        description:
          reason ??
          t("fileAttachments.deleteError", {
            filename: deleteData.filename,
          }),
        variant: "destructive",
      });
      setPendingDelete(null);
      onDeleteError?.(error);
    },
  });

  const handleRemove = useCallback((item: AttachmentItem) => {
    if (!item.meta?.id) return;

    setPendingDelete({
      attachmentId: item.meta.id,
      index: item.index,
      filename: item.field.filename,
      s3Key: item.field.s3_key,
    });
  }, []);

  const confirmDelete = useCallback(() => {
    if (!pendingDelete) return;
    deleteMutation.mutate(pendingDelete);
  }, [pendingDelete, deleteMutation]);

  const cancelDelete = useCallback(() => setPendingDelete(null), []);

  return {
    pendingDelete,
    confirmDelete,
    cancelDelete,
    handleRemove,
    isDeleting: deleteMutation.isPending,
    operationProgress,
  };
};
